import { FunctionComponent, useCallback, useState } from "react";
import { Form } from "@ui-kit/Form/Form";
import { useAppDispatch, useAppSelector } from "@store/store";
import { useTranslation } from "react-i18next";
import { FormStatus } from "../../../../components/formFields/FormStatus";
import { AuthSubmitButton } from "../../components/AuthSubmitButton/AuthSubmitButton";
import { AuthInstructions } from "../../components/AuthInstructions/AuthInstructions";
import { AuthError } from "../../components/AuthError/AuthError";
import { resetPasswordAction } from "@store/auth/actions/resetPasswordAction";
import { PasswordField } from "../../../../components/formFields/PasswordField/PasswordField";
import { selectResetPasswordToken } from "@store/sessionReducer";
interface ResetPasswordProps {}

const ResetPassword: FunctionComponent<ResetPasswordProps> = () => {
  const [state, setState] = useState<{ status: FormStatus }>({
    status: "idle",
  });
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const resetPasswordToken = useAppSelector(selectResetPasswordToken);

  const handleSubmit = useCallback(
    async (values: { newPassword: string; confirmPassword: string }) => {
      setState({ status: "fetching" });
      try {
        await dispatch(
          resetPasswordAction({
            resetPasswordToken: resetPasswordToken ?? "",
            newPassword: values.newPassword,
          }),
        );
        setState({ status: "success" });
      } catch (_error) {
        setState({ status: "error" });
      }
    },
    [dispatch, resetPasswordToken],
  );

  const disabled = state.status === "fetching" || state.status === "success";

  return (
    <>
      <AuthInstructions />
      <div className="form-container">
        <Form
          form={form}
          name="resetPassword"
          layout="vertical"
          onFinish={handleSubmit}
          disabled={disabled}
          initialValues={{ newPassword: "", confirmPassword: "" }}>
          <PasswordField
            name="newPassword"
            type="new-password"
            disabled={disabled}
            rules={[
              {
                min: 6,
                message: t("forms.newPasswordField.minLength"),
              },
            ]}
          />
          <PasswordField
            name="confirmPassword"
            type="confirm-password"
            disabled={disabled}
            label={t("forms.confirmPasswordField.label")}
            rules={[
              {
                validator: (_rule, value) => {
                  if (value !== form.getFieldValue("newPassword")) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
                message: t("forms.confirmPasswordField.match"),
              },
            ]}
          />

          <div className="form-button-container">
            <AuthSubmitButton
              label={t("screens.auth.resetPassword.submit")}
              disabled={disabled}
            />
          </div>
        </Form>
      </div>

      {state.status === "error" && <AuthError />}
    </>
  );
};

export { ResetPassword };
